import React, { useState } from 'react';
import { Bell, AlertTriangle, Clock, Activity, Heart, Zap, Users, Edit2, Trash2, Plus, Save, X } from 'lucide-react';

interface Resident {
  name: string;
  age: number;
  address: string;
  emergencyContact: string;
  careLevel: string;
  primaryConditions: string[];
}

interface AlertConfigurationProps {
  resident: Resident;
}

interface AlertRule {
  id: string;
  name: string;
  type: 'MOVEMENT' | 'LOCATION' | 'TIME' | 'PATTERN' | 'VITAL';
  condition: string;
  threshold: string | number;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  notifyList: string[];
  isActive: boolean;
  description: string;
}

interface Contact {
  id: string;
  name: string;
  role: string;
  phone: string;
  email: string;
  notificationPreference: 'SMS' | 'EMAIL' | 'BOTH';
}

const AlertConfiguration: React.FC<AlertConfigurationProps> = ({ resident }) => {
  // Mock data for alert rules
  const initialAlertRules: AlertRule[] = [
    {
      id: 'rule-1',
      name: 'Extended Bathroom Visit',
      type: 'LOCATION',
      condition: 'Time in bathroom exceeds',
      threshold: '15 minutes',
      priority: 'MEDIUM',
      notifyList: ['contact-1', 'contact-2'],
      isActive: true,
      description: 'Alert triggered when resident spends unusually long time in bathroom, which may indicate a fall or other emergency.'
    },
    {
      id: 'rule-2',
      name: 'Reduced Morning Activity',
      type: 'MOVEMENT',
      condition: 'No movement detected between',
      threshold: '7:00 AM - 9:00 AM',
      priority: 'HIGH',
      notifyList: ['contact-1', 'contact-3'],
      isActive: true,
      description: 'Alert when no movement is detected during typical morning hours, which may indicate the resident hasn\'t gotten out of bed.'
    },
    {
      id: 'rule-3',
      name: 'Unusual Nighttime Activity',
      type: 'PATTERN',
      condition: 'Bathroom visits at night exceed',
      threshold: '3 times',
      priority: 'MEDIUM',
      notifyList: ['contact-2'],
      isActive: true,
      description: 'Alert when bathroom visits during nighttime hours (10 PM - 6 AM) exceed normal pattern.'
    },
    {
      id: 'rule-4',
      name: 'Missed Medication Time',
      type: 'TIME',
      condition: 'No medicine cabinet access at scheduled time',
      threshold: 'Within 1 hour',
      priority: 'HIGH',
      notifyList: ['contact-1', 'contact-2', 'contact-3'],
      isActive: false,
      description: 'Alert when resident does not access medicine cabinet within 1 hour of scheduled medication times.'
    },
    {
      id: 'rule-5',
      name: 'Extended Inactivity',
      type: 'MOVEMENT',
      condition: 'No significant movement for',
      threshold: '3 hours',
      priority: 'HIGH',
      notifyList: ['contact-1'],
      isActive: true,
      description: 'Alert when no significant movement is detected in any room for an extended period during daytime hours.'
    }
  ];

  // Mock data for emergency contacts
  const contacts: Contact[] = [
    {
      id: 'contact-1',
      name: 'Michael Chen',
      role: 'Son',
      phone: '(555) 123-4567',
      email: 'michael.chen@example.com',
      notificationPreference: 'BOTH'
    },
    {
      id: 'contact-2',
      name: 'Lisa Rodriguez',
      role: 'Home Nurse',
      phone: '(555) 234-5678',
      email: 'lisa.rodriguez@careprovider.com',
      notificationPreference: 'SMS'
    },
    {
      id: 'contact-3',
      name: 'Dr. Sarah Johnson',
      role: 'Primary Physician',
      phone: '(555) 345-6789',
      email: 'sarah.johnson@medcenter.org',
      notificationPreference: 'EMAIL'
    }
  ];

  const [alertRules, setAlertRules] = useState<AlertRule[]>(initialAlertRules);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  const [formData, setFormData] = useState<Partial<AlertRule>>({
    name: '',
    type: 'MOVEMENT',
    condition: '',
    threshold: '',
    priority: 'MEDIUM',
    notifyList: [],
    isActive: true,
    description: ''
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleNotifyListChange = (contactId: string) => {
    const currentList = [...(formData.notifyList || [])];
    const index = currentList.indexOf(contactId);

    if (index >= 0) {
      currentList.splice(index, 1);
    } else {
      currentList.push(contactId);
    }

    setFormData({
      ...formData,
      notifyList: currentList
    });
  };

  const handleAddRule = () => {
    setFormData({
      name: '',
      type: 'MOVEMENT',
      condition: '',
      threshold: '',
      priority: 'MEDIUM',
      notifyList: [],
      isActive: true,
      description: ''
    });
    setIsAddingNew(true);
    setEditingRule(null);
  };

  const handleEditRule = (rule: AlertRule) => {
    setFormData(rule);
    setEditingRule(rule);
    setIsAddingNew(false);
  };

  const handleSaveRule = () => {
    if (!formData.name || !formData.condition || !formData.threshold) {
      alert('Please fill in all required fields');
      return;
    }

    if (isAddingNew) {
      const newRule: AlertRule = {
        ...formData as Required<Omit<AlertRule, 'id'>>,
        id: `rule-${Date.now()}`
      };
      
      setAlertRules([...alertRules, newRule]);
    } else if (editingRule) {
      const updatedRules = alertRules.map(rule => 
        rule.id === editingRule.id ? { ...formData, id: rule.id } as AlertRule : rule
      );
      
      setAlertRules(updatedRules);
    }
    
    setIsAddingNew(false);
    setEditingRule(null);
  };

  const handleCancelEdit = () => {
    setIsAddingNew(false);
    setEditingRule(null);
  };

  const handleToggleRule = (id: string) => {
    setAlertRules(alertRules.map(rule => 
      rule.id === id ? { ...rule, isActive: !rule.isActive } : rule
    ));
  };

  const handleDeleteRule = (id: string) => {
    if (window.confirm('Are you sure you want to delete this alert rule?')) {
      setAlertRules(alertRules.filter(rule => rule.id !== id));
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'MOVEMENT':
        return <Activity className="text-green-500" />;
      case 'LOCATION':
        return <Users className="text-blue-500" />;
      case 'TIME':
        return <Clock className="text-yellow-500" />;
      case 'PATTERN':
        return <Zap className="text-purple-500" />;
      case 'VITAL':
        return <Heart className="text-red-500" />;
      default:
        return <Bell className="text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getContactNames = (contactIds: string[]) => {
    return contactIds.map(id => 
      contacts.find(contact => contact.id === id)?.name || 'Unknown'
    ).join(', ');
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-semibold">Alert Configuration</h3>
          <p className="text-gray-600">Customize alert settings for {resident.name}</p>
        </div>
        <button
          onClick={handleAddRule}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus size={16} className="mr-2" />
          Add Alert Rule
        </button>
      </div>

      {/* Add/Edit Form */}
      {(isAddingNew || editingRule) && (
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex justify-between items-center mb-4 pb-2 border-b">
            <h3 className="font-medium">{isAddingNew ? 'Create New Alert Rule' : 'Edit Alert Rule'}</h3>
            <button onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700">
              <X size={20} />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alert Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  placeholder="e.g., Extended Bathroom Visit"
                  className="w-full border rounded-lg p-2"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alert Type
                </label>
                <select
                  name="type"
                  value={formData.type || 'MOVEMENT'}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="MOVEMENT">Movement Detection</option>
                  <option value="LOCATION">Location Based</option>
                  <option value="TIME">Time-based</option>
                  <option value="PATTERN">Pattern Change</option>
                  <option value="VITAL">Vital Signs</option>
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condition
                </label>
                <input
                  type="text"
                  name="condition"
                  value={formData.condition || ''}
                  onChange={handleInputChange}
                  placeholder="e.g., Time in bathroom exceeds"
                  className="w-full border rounded-lg p-2"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Threshold
                </label>
                <input
                  type="text"
                  name="threshold"
                  value={formData.threshold || ''}
                  onChange={handleInputChange}
                  placeholder="e.g., 15 minutes"
                  className="w-full border rounded-lg p-2"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority || 'MEDIUM'}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>
              
              <div className="mb-4">
                <label className="flex items-center text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleCheckboxChange}
                    className="mr-2 rounded text-blue-500"
                  />
                  Active Alert
                </label>
              </div>
            </div>
            
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  className="w-full border rounded-lg p-2 h-24"
                  placeholder="Describe when this alert should trigger and why"
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notify These Contacts
                </label>
                <div className="space-y-2 border rounded-lg p-3 max-h-48 overflow-y-auto">
                  {contacts.map(contact => (
                    <div key={contact.id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`contact-${contact.id}`}
                        checked={(formData.notifyList || []).includes(contact.id)}
                        onChange={() => handleNotifyListChange(contact.id)}
                        className="mr-2 rounded text-blue-500"
                      />
                      <label htmlFor={`contact-${contact.id}`} className="text-sm">
                        {contact.name} ({contact.role})
                      </label>
                      <span className="ml-auto text-xs text-gray-500">
                        {contact.notificationPreference === 'BOTH' 
                          ? 'SMS & Email' 
                          : contact.notificationPreference}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end mt-4">
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 border border-gray-300 rounded-lg mr-2 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRule}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  <Save size={16} className="inline-block mr-1" />
                  {isAddingNew ? 'Create Alert' : 'Update Alert'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alert Rules */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b flex items-center">
          <AlertTriangle className="text-yellow-500 mr-2" />
          <h3 className="font-medium">Configured Alert Rules</h3>
        </div>
        
        {alertRules.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {alertRules.map(rule => (
              <li key={rule.id} className={`p-4 ${!rule.isActive ? 'bg-gray-50' : ''}`}>
                <div className="flex justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      {getTypeIcon(rule.type)}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center">
                        <h4 className="font-medium">{rule.name}</h4>
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${getPriorityColor(rule.priority)}`}>
                          {rule.priority}
                        </span>
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                          rule.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {rule.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 mt-1">
                        <span className="font-medium">Condition:</span> {rule.condition} {rule.threshold}
                      </p>
                      
                      <p className="text-sm text-gray-600 mt-1">
                        <span className="font-medium">Notify:</span> {getContactNames(rule.notifyList)}
                      </p>
                      
                      {rule.description && (
                        <p className="text-sm text-gray-500 mt-2 bg-gray-50 p-2 rounded">
                          {rule.description}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => handleToggleRule(rule.id)}
                      className={`text-sm px-2 py-1 rounded ${
                        rule.isActive 
                          ? 'text-red-500 hover:bg-red-50' 
                          : 'text-green-500 hover:bg-green-50'
                      }`}
                      title={rule.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {rule.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => handleEditRule(rule)}
                      className="text-blue-500 hover:text-blue-700"
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteRule(rule.id)}
                      className="text-red-500 hover:text-red-700"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-6 text-center text-gray-500">
            No alert rules configured. Click "Add Alert Rule" to create one.
          </div>
        )}
      </div>

      {/* Emergency Contacts Section */}
      <div className="mt-6 bg-white rounded-lg shadow">
        <div className="p-4 border-b flex items-center">
          <Users className="text-blue-500 mr-2" />
          <h3 className="font-medium">Emergency Contacts</h3>
        </div>
        
        <ul className="divide-y divide-gray-100">
          {contacts.map(contact => (
            <li key={contact.id} className="p-4">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-medium">{contact.name}</h4>
                  <p className="text-sm text-gray-500">{contact.role}</p>
                </div>
                
                <div className="flex flex-col text-sm text-right">
                  <span>{contact.phone}</span>
                  <span>{contact.email}</span>
                  <span className="text-xs text-gray-500 mt-1">
                    Notifications: {contact.notificationPreference === 'BOTH' 
                      ? 'SMS & Email' 
                      : contact.notificationPreference}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default AlertConfiguration; 